"use client";

import { LiveDot } from "./chrome";

/**
 * Illustrative data visuals for the dev landing pages. All values are static
 * mock data rendered with plain CSS - they exist to show payload shapes and
 * what you can build, not to stream real quotes.
 */

/** Shared shell: mono title bar with a live dot, dark body. */
export function MockFrame({
  title,
  children,
  live = true,
}: {
  title: string;
  children: React.ReactNode;
  live?: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-[#1f2020] bg-[#0d0e0e] shadow-2xl shadow-black/50">
      <div className="flex items-center justify-between border-b border-[#1c1d1d] px-4 py-2.5">
        <span className="truncate font-mono text-xs text-[#8d918f]">{title}</span>
        {live ? (
          <span className="flex shrink-0 items-center gap-1.5 font-mono text-[10px] text-[#8d918f]">
            <LiveDot />
            live
          </span>
        ) : null}
      </div>
      {children}
    </div>
  );
}

/** Strike rows for the illustrative GEX profile. Values are % of max |GEX|. */
const GEX_PROFILE: { strike: string; value: number; label?: string }[] = [
  { strike: "632", value: 18 },
  { strike: "630", value: 92, label: "Call wall" },
  { strike: "627", value: 44 },
  { strike: "625", value: 58 },
  { strike: "622", value: 14 },
  { strike: "620", value: -10, label: "Gamma flip 620.5" },
  { strike: "617", value: -38 },
  { strike: "615", value: -86, label: "Put wall" },
  { strike: "612", value: -28 },
];

/** Net-GEX-by-strike profile rendered straight from the payload shape. */
export function GexProfileMock() {
  return (
    <MockFrame title="SPY &middot; net GEX by strike">
      <div className="flex flex-col gap-2 p-4 sm:p-5">
        {GEX_PROFILE.map((row) => (
          <div key={row.strike} className="flex items-center gap-3">
            <span className="w-9 shrink-0 text-right font-mono text-xs text-[#8d918f]">{row.strike}</span>
            <div className="relative h-4 flex-1">
              <span className="absolute inset-y-0 left-1/2 w-px bg-[#2a2b2b]" aria-hidden="true" />
              <span
                className={`absolute inset-y-0.5 rounded-sm ${
                  row.value >= 0 ? "left-1/2 bg-emerald-500/70" : "right-1/2 bg-red-500/50"
                }`}
                style={{ width: `${Math.abs(row.value) / 2}%` }}
                aria-hidden="true"
              />
              {row.label ? (
                <span
                  className={`absolute top-1/2 -translate-y-1/2 whitespace-nowrap font-mono text-[10px] text-[#d4d5d5] ${
                    row.value >= 0 ? "right-2" : "left-2"
                  }`}
                >
                  {row.label}
                </span>
              ) : null}
            </div>
          </div>
        ))}
        <div className="mt-2 flex items-center justify-center gap-5 border-t border-[#1c1d1d] pt-3 font-mono text-[10px] text-[#8d918f]">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-emerald-500/70" aria-hidden="true" /> call gamma
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-red-500/50" aria-hidden="true" /> put gamma
          </span>
          <span>spot 622.41</span>
        </div>
      </div>
    </MockFrame>
  );
}

/** Illustrative net premium drift: calls above the axis, puts below (% of max). */
const DRIFT_BARS: { call: number; put: number }[] = [
  { call: 30, put: 18 },
  { call: 52, put: 24 },
  { call: 44, put: 40 },
  { call: 22, put: 58 },
  { call: 16, put: 72 },
  { call: 38, put: 46 },
  { call: 62, put: 28 },
  { call: 84, put: 20 },
  { call: 70, put: 34 },
  { call: 92, put: 16 },
  { call: 58, put: 26 },
  { call: 76, put: 12 },
];

export function FlowDriftMock() {
  return (
    <MockFrame title="NVDA &middot; net premium drift &middot; 1m bars">
      <div className="p-4 sm:p-5">
        <div className="flex h-44 items-stretch gap-1.5">
          {DRIFT_BARS.map((bar, i) => (
            <div key={i} className="flex flex-1 flex-col">
              <div className="flex flex-1 items-end">
                <span
                  className="w-full rounded-t-sm bg-emerald-500/70"
                  style={{ height: `${bar.call}%` }}
                  aria-hidden="true"
                />
              </div>
              <span className="my-px h-px w-full bg-[#2a2b2b]" aria-hidden="true" />
              <div className="flex flex-1 items-start">
                <span
                  className="w-full rounded-b-sm bg-red-500/50"
                  style={{ height: `${bar.put}%` }}
                  aria-hidden="true"
                />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-center gap-5 border-t border-[#1c1d1d] pt-3 font-mono text-[10px] text-[#8d918f]">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-emerald-500/70" aria-hidden="true" /> net call premium
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-red-500/50" aria-hidden="true" /> net put premium
          </span>
        </div>
      </div>
    </MockFrame>
  );
}

const PRINTS: { time: string; type: string; side: string; strike: string; exp: string; premium: string; call: boolean }[] = [
  { time: "14:21:08", type: "SWEEP", side: "A", strike: "185 C", exp: "08/08", premium: "$1.42M", call: true },
  { time: "14:20:51", type: "BLOCK", side: "B", strike: "175 P", exp: "08/15", premium: "$880K", call: false },
  { time: "14:18:42", type: "SWEEP", side: "A", strike: "190 C", exp: "08/22", premium: "$3.21M", call: true },
  { time: "14:17:19", type: "SPLIT", side: "M", strike: "180 P", exp: "08/08", premium: "$548K", call: false },
  { time: "14:15:03", type: "BLOCK", side: "A", strike: "187.5 C", exp: "08/15", premium: "$2.10M", call: true },
];

export function FlowTradesMock() {
  return (
    <MockFrame title="NVDA &middot; significant prints">
      <div className="overflow-x-auto p-2 sm:p-3">
        <table className="w-full font-mono text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#5b5b5b]">
              <th className="px-2 py-1.5">Time</th>
              <th className="px-2 py-1.5">Type</th>
              <th className="px-2 py-1.5">Side</th>
              <th className="px-2 py-1.5">Contract</th>
              <th className="px-2 py-1.5">Exp</th>
              <th className="px-2 py-1.5 text-right">Premium</th>
            </tr>
          </thead>
          <tbody>
            {PRINTS.map((print) => (
              <tr key={print.time} className="border-t border-[#1c1d1d] text-[#d4d5d5]">
                <td className="px-2 py-2 text-[#8d918f]">{print.time}</td>
                <td className="px-2 py-2">
                  <span className="rounded border border-[#3a3c3b] px-1.5 py-0.5 text-[10px] text-[#d4d5d5]">
                    {print.type}
                  </span>
                </td>
                <td className="px-2 py-2 text-[#8d918f]">{print.side}</td>
                <td className={`px-2 py-2 ${print.call ? "text-emerald-300" : "text-red-300"}`}>{print.strike}</td>
                <td className="px-2 py-2 text-[#8d918f]">{print.exp}</td>
                <td className="px-2 py-2 text-right text-[#f2f4f3]">{print.premium}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </MockFrame>
  );
}

/** Net GEX intensity per strike (rows) and expiration (cols), -1..1. */
const HEATMAP_EXPS = ["08/08", "08/15", "08/22", "09/19"];
const HEATMAP_ROWS: { strike: string; cells: number[] }[] = [
  { strike: "632", cells: [0.2, 0.35, 0.15, 0.1] },
  { strike: "630", cells: [0.95, 0.7, 0.45, 0.3] },
  { strike: "627", cells: [0.4, 0.5, 0.25, 0.15] },
  { strike: "625", cells: [0.55, 0.6, 0.35, 0.2] },
  { strike: "622", cells: [0.1, 0.15, 0.05, 0.05] },
  { strike: "620", cells: [-0.2, -0.1, -0.15, -0.05] },
  { strike: "617", cells: [-0.45, -0.3, -0.35, -0.2] },
  { strike: "615", cells: [-0.9, -0.75, -0.5, -0.4] },
  { strike: "612", cells: [-0.3, -0.25, -0.2, -0.1] },
];

export function GexHeatmapMock() {
  return (
    <MockFrame title="SPY &middot; net GEX heatmap &middot; strike &times; expiration">
      <div className="p-4 sm:p-5">
        <div className="grid grid-cols-[2.5rem_repeat(4,1fr)] gap-1">
          <span aria-hidden="true" />
          {HEATMAP_EXPS.map((exp) => (
            <span key={exp} className="pb-1 text-center font-mono text-[10px] text-[#8d918f]">
              {exp}
            </span>
          ))}
          {HEATMAP_ROWS.map((row) => (
            <FragmentRow key={row.strike} strike={row.strike} cells={row.cells} />
          ))}
        </div>
        <div className="mt-3 flex items-center justify-center gap-5 border-t border-[#1c1d1d] pt-3 font-mono text-[10px] text-[#8d918f]">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-emerald-500/70" aria-hidden="true" /> call gamma
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-red-500/50" aria-hidden="true" /> put gamma
          </span>
        </div>
      </div>
    </MockFrame>
  );
}

function FragmentRow({ strike, cells }: { strike: string; cells: number[] }) {
  return (
    <>
      <span className="self-center text-right font-mono text-[10px] text-[#8d918f]">{strike}</span>
      {cells.map((cell, i) => (
        <span
          key={i}
          className="h-6 rounded-sm"
          style={{
            backgroundColor:
              cell >= 0 ? `rgba(16,185,129,${0.08 + cell * 0.6})` : `rgba(239,68,68,${0.08 + Math.abs(cell) * 0.5})`,
          }}
          aria-hidden="true"
        />
      ))}
    </>
  );
}

const GEX_EVENTS: { time: string; kind: "FLIP" | "WALL"; text: string }[] = [
  { time: "14:32:05", kind: "FLIP", text: "SPY crossed 620.5 into negative gamma" },
  { time: "13:58:41", kind: "WALL", text: "NVDA call wall migrated 185 \u2192 190" },
  { time: "13:12:09", kind: "WALL", text: "QQQ put wall reinforced at 545" },
  { time: "12:47:33", kind: "FLIP", text: "AMD crossed 162.5 into positive gamma" },
  { time: "11:20:18", kind: "WALL", text: "SPY call wall migrated 628 \u2192 630" },
];

export function GexEventsMock() {
  return (
    <MockFrame title="/v1/gex/events &middot; market-wide">
      <ul className="flex flex-col p-2 sm:p-3">
        {GEX_EVENTS.map((event) => (
          <li
            key={event.time}
            className="flex items-baseline gap-3 border-t border-[#1c1d1d] px-2 py-2.5 font-mono text-xs first:border-t-0"
          >
            <span className="shrink-0 text-[#8d918f]">{event.time}</span>
            <span
              className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] tracking-wide ${
                event.kind === "FLIP"
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                  : "border-[#3a3c3b] text-[#d4d5d5]"
              }`}
            >
              {event.kind}
            </span>
            <span className="min-w-0 text-[#d4d5d5]">{event.text}</span>
          </li>
        ))}
      </ul>
    </MockFrame>
  );
}

/** Candles as % offsets of the chart height. */
const CANDLES: { top: number; height: number; wickTop: number; wickHeight: number; up: boolean }[] = [
  { top: 62, height: 10, wickTop: 58, wickHeight: 18, up: true },
  { top: 56, height: 9, wickTop: 52, wickHeight: 16, up: true },
  { top: 58, height: 7, wickTop: 54, wickHeight: 14, up: false },
  { top: 50, height: 11, wickTop: 46, wickHeight: 18, up: true },
  { top: 44, height: 8, wickTop: 40, wickHeight: 15, up: true },
  { top: 46, height: 9, wickTop: 43, wickHeight: 15, up: false },
  { top: 38, height: 10, wickTop: 34, wickHeight: 17, up: true },
  { top: 40, height: 7, wickTop: 36, wickHeight: 14, up: false },
  { top: 33, height: 9, wickTop: 29, wickHeight: 16, up: true },
  { top: 27, height: 8, wickTop: 23, wickHeight: 15, up: true },
  { top: 30, height: 8, wickTop: 26, wickHeight: 14, up: false },
  { top: 24, height: 9, wickTop: 20, wickHeight: 16, up: true },
  { top: 27, height: 7, wickTop: 24, wickHeight: 12, up: false },
  { top: 22, height: 8, wickTop: 18, wickHeight: 14, up: true },
];

const LEVELS: { top: number; label: string; tone: "call" | "flip" | "put" }[] = [
  { top: 14, label: "Call wall 630", tone: "call" },
  { top: 52, label: "Gamma flip 620.5", tone: "flip" },
  { top: 86, label: "Put wall 615", tone: "put" },
];

/** TradingView-style candles with GEX levels drawn on top. */
export function TvOverlayMock() {
  return (
    <MockFrame title="TradingView &middot; SPY 5m &middot; QuantWheel overlay">
      <div className="relative m-4 h-64 sm:m-5">
        {LEVELS.map((level) => (
          <div key={level.label} className="absolute inset-x-0" style={{ top: `${level.top}%` }} aria-hidden="true">
            <div
              className={`border-t border-dashed ${
                level.tone === "call"
                  ? "border-emerald-400/60"
                  : level.tone === "put"
                    ? "border-red-400/50"
                    : "border-[#9aa09e]/50"
              }`}
            />
          </div>
        ))}
        <div className="absolute inset-y-0 left-0 right-24 flex items-stretch gap-[3px] sm:gap-1">
          {CANDLES.map((candle, i) => (
            <div key={i} className="relative flex-1">
              <span
                className={`absolute left-1/2 w-px -translate-x-1/2 ${candle.up ? "bg-emerald-400/60" : "bg-red-400/50"}`}
                style={{ top: `${candle.wickTop}%`, height: `${candle.wickHeight}%` }}
                aria-hidden="true"
              />
              <span
                className={`absolute inset-x-0 mx-auto w-full max-w-[10px] rounded-[1px] ${
                  candle.up ? "bg-emerald-500/80" : "bg-red-500/70"
                }`}
                style={{ top: `${candle.top}%`, height: `${candle.height}%` }}
                aria-hidden="true"
              />
            </div>
          ))}
        </div>
        {LEVELS.map((level) => (
          <span
            key={level.label}
            className={`absolute right-0 -translate-y-1/2 rounded border px-1.5 py-0.5 font-mono text-[10px] ${
              level.tone === "call"
                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                : level.tone === "put"
                  ? "border-red-500/30 bg-red-500/10 text-red-300"
                  : "border-[#3a3c3b] bg-[#131414] text-[#d4d5d5]"
            }`}
            style={{ top: `${level.top}%` }}
          >
            {level.label}
          </span>
        ))}
      </div>
    </MockFrame>
  );
}

const SCREENER_ROWS: { ticker: string; contract: string; yield_: string; delta: string; annual: string }[] = [
  { ticker: "NVDA", contract: "08/15 175P", yield_: "1.9%", delta: "-0.22", annual: "69%" },
  { ticker: "AMD", contract: "08/15 155P", yield_: "1.6%", delta: "-0.19", annual: "58%" },
  { ticker: "SPY", contract: "08/22 610P", yield_: "0.8%", delta: "-0.15", annual: "24%" },
  { ticker: "TSLA", contract: "08/08 300P", yield_: "2.4%", delta: "-0.25", annual: "88%" },
];

export function ScreenerMock() {
  return (
    <MockFrame title="/v1/screener &middot; puts &middot; delta &le; 0.25">
      <div className="overflow-x-auto p-2 sm:p-3">
        <table className="w-full font-mono text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#5b5b5b]">
              <th className="px-2 py-1.5">Ticker</th>
              <th className="px-2 py-1.5">Contract</th>
              <th className="px-2 py-1.5 text-right">Premium</th>
              <th className="px-2 py-1.5 text-right">Delta</th>
              <th className="px-2 py-1.5 text-right">Annualized</th>
            </tr>
          </thead>
          <tbody>
            {SCREENER_ROWS.map((row) => (
              <tr key={row.ticker} className="border-t border-[#1c1d1d] text-[#d4d5d5]">
                <td className="px-2 py-2 text-[#f2f4f3]">{row.ticker}</td>
                <td className="px-2 py-2">{row.contract}</td>
                <td className="px-2 py-2 text-right text-emerald-300">{row.yield_}</td>
                <td className="px-2 py-2 text-right text-[#8d918f]">{row.delta}</td>
                <td className="px-2 py-2 text-right text-[#f2f4f3]">{row.annual}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </MockFrame>
  );
}
